import {Component, Vue} from "vue-property-decorator";
import {mapActions, mapGetters} from "vuex";
import {AxiosInstance} from "axios";
import {HdsActions} from "@/store/modules/hds/actions";
import {ProcessComponentModel} from "@/store/modules/hds/Model";
import {AssetsProcessProfile, ProcessProfile} from "@/store/modules/hcs/processProfile/model";
import {ProcessImplementation} from "@/store/modules/hcs/processImplementation/model";
import gql from "graphql-tag";

/**
 * プロセスプロファイルと関連する要素のためのビュー
 */
export interface ProcessProfileView extends ProcessProfile {
  processImplementation: ProcessImplementation;
  localProcessComponentModel:  ProcessComponentModel;
}

@Component({
  computed: {
    ...mapGetters({
      _hcsClient: 'hcs/client',
    }),
  },
  methods: {
    ...mapActions({
      _gqlQuery: 'hds/gqlQuery',
    }),
  }
})
export default class extends Vue {
  /** hcs/hcsClient */
  readonly _hcsClient!: AxiosInstance;

  /** hds/gqlQuery */
  readonly _gqlQuery!: HdsActions['gqlQuery'];

  /** ProcessImplementation のキャッシュ <プロセス実装ID, プロセス実装を返す Promise> */
  readonly processImplementationCache = new Map<string, Promise<ProcessImplementation>>();

  /**
   * プロセスプロファイルを読み込みます
   *
   * @param processProfileId プロセスプロファイルID
   */
  async loadProcessProfile(processProfileId: string): Promise<ProcessProfileView> {
    const {data: processProfile} = await this._hcsClient.get(`/process_profiles/${processProfileId}`);
    const processImplementation = await this.loadProcessImplementation(processProfile.processImplementationId);
    const {localProcessComponentModel} = await this._gqlQuery({
        query: gql`
            query FindProcessComponentModel(
                $processComponentModelId: ID!,
            ) {
                localProcessComponentModel: processComponentModel(processComponentModelId: $processComponentModelId) {
                    id
                    name
                    description
                }
            }`,
      variables: {
        processComponentModelId: processImplementation.processComponentModelId
      }
    });

    return {
      ...processProfile,
      processImplementation,
      localProcessComponentModel
    };
  }

  /**
   * 他事業者のプロセスプロファイルを読み込みます
   *
   * @param processProfileId プロセスプロファイルID
   */
  async loadAssetsProcessProfile(processProfileId: string): Promise<AssetsProcessProfile> {
    const {data: processProfile} = await this._hcsClient.get(`/assets/process_profiles/${processProfileId}`);
    return processProfile;
  }

  /**
   * プロセス実装IDからプロセス実装を取得します
   * 取得したプロセス実装はキャッシュされます。
   *
   * @param processImplementationId プロセス実装ID
   * @private
   */
  private loadProcessImplementation(processImplementationId: string): Promise<ProcessImplementation> {
    if(this.processImplementationCache.has(processImplementationId)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.processImplementationCache.get(processImplementationId)!;
    }
    const promise = this._hcsClient.get(`/process_implementations/${processImplementationId}`).then(result => result.data);
    this.processImplementationCache.set(processImplementationId, promise);
    return promise;
  }
}
